import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { getEventById } from '../../calendar/services/calendarService';
import { resolveEventConsultationNavigation } from '../utils/eventConsultationNavigation';

export const AppointmentConsultationRedirect: React.FC = () => {
  const { t } = useTranslation(['consultations', 'common', 'nav']);
  const navigate = useNavigate();
  const location = useLocation();
  const { petId: legacyAssetId, assetId, appointmentId } = useParams<{
    petId?: string;
    assetId?: string;
    appointmentId: string;
  }>();

  const [isNotFound, setIsNotFound] = React.useState(false);

  const returnTo = React.useMemo(() => {
    const state = location.state as { returnTo?: string } | null;
    return state?.returnTo;
  }, [location.state]);

  const fallbackPath = returnTo || '/calendar';

  const fallbackLabel = React.useMemo(() => {
    if (returnTo?.startsWith('/calendar')) {
      return t('calendar', { ns: 'nav', defaultValue: 'Calendar' });
    }

    if (returnTo?.startsWith('/consultations')) {
      return t('consultations', { defaultValue: 'Consultations' });
    }

    if (returnTo?.startsWith('/assets')) {
      return t('back', { ns: 'common', defaultValue: 'Back' });
    }

    return t('calendar', { ns: 'nav', defaultValue: 'Calendar' });
  }, [returnTo, t]);

  React.useEffect(() => {
    let cancelled = false;

    const redirect = async () => {
      if (!appointmentId) {
        setIsNotFound(true);
        return;
      }

      try {
        const loadedEvent = await getEventById(appointmentId);

        if (!loadedEvent) {
          if (!cancelled) {
            setIsNotFound(true);
          }
          return;
        }

        const eventAssetIds = loadedEvent.assetIds && loadedEvent.assetIds.length > 0
          ? loadedEvent.assetIds
          : (loadedEvent.assetId ? [loadedEvent.assetId] : []);

        const requestedAssetId = assetId || legacyAssetId || '';
        if (requestedAssetId && !eventAssetIds.includes(requestedAssetId)) {
          if (!cancelled) {
            setIsNotFound(true);
          }
          return;
        }

        const target = await resolveEventConsultationNavigation(loadedEvent, returnTo);

        if (cancelled) {
          return;
        }

        navigate(`${target.pathname}${target.search || ''}`, {
          replace: true,
          state: target.state,
        });
      } catch {
        if (!cancelled) {
          setIsNotFound(true);
        }
      }
    };

    void redirect();

    return () => {
      cancelled = true;
    };
  }, [appointmentId, assetId, legacyAssetId, navigate, returnTo]);

  if (isNotFound) {
    return (
      <div className="company-section" style={{ marginTop: 24 }}>
        <h3>{t('consultation', { defaultValue: 'Consultation' })}</h3>
        <p>{t('consultationRouteNotFound', { defaultValue: 'Consultation appointment was not found.' })}</p>
        <button className="btn btn-secondary" type="button" onClick={() => navigate(fallbackPath)}>
          {fallbackLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="company-section" style={{ marginTop: 24 }}>
      <Spinner />
    </div>
  );
};

export default AppointmentConsultationRedirect;
